import { CSSResultGroup, html, TemplateResult } from 'lit';
import {
  customElement,
  queryAssignedElements,
  state,
  property,
} from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { SlotController } from '../../controllers/slot-controller';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './idb-step-indicator.css.lit';

/**
 * The IdbStepIndicator component.
 * @extends OutlineElement
 * @property ...
 */

@customElement('idb-step-indicator')
export class IdbStepIndicator extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotController(
    this, // This, the host element.
    false // To shift or not to shift LightDom nodes to ShadowDOM.
  );

  @property({ type: String, attribute: 'step' })
  step: string;

  @property({ type: Boolean, attribute: 'clickable-steps' })
  clickableSteps = false;

  @queryAssignedElements({ slot: 'step-panels' })
  stepPanels: HTMLElement[];

  @state() panels: HTMLElement[];
  @state() indicator = 1;

  panelElement: HTMLElement[];

  firstUpdated() {
    this.panels = this.stepPanels;
    this.createSectionPanels();
    if (this.step) {
      this.indicator = parseInt(this.step);
      this.handleChangeStep(this.indicator);
    }
  }

  handleChangeStep(index: number) {
    this.indicator = index;

    this.stepPanels.map((panel, i) => {
      if (i + 1 === index) {
        panel.classList.add('active');
      } else {
        panel.classList.remove('active');
      }
    });
  }

  stepsTemplate(): TemplateResult | null {
    if (!this.panels) return null;

    return html`
      ${this.panels.map((item, index) => {
        const indexValue = index + 1;
        const stepClasses = {
          'step-indicator__step': true,
          'active': this.indicator === indexValue,
          'checked': this.indicator > indexValue,
        };
        return html`
          <div class=${classMap(stepClasses)}>
            <div class="step-indicator__circle">
              ${
                indexValue > 1
                  ? html`<span class="step-indicator__line"></span>`
                  : html`<span class="step-indicator__line transparent"></span>`
              }
              ${
                this.clickableSteps
                  ? html`
                  ${
                    indexValue < this.indicator
                      ? html`
                  <button 
                    class="step-indicator__checked"
                    @click=${() => this.handleChangeStep(indexValue)}>
                    <outline-icon name="check-lg" library="system" size="18px"></outline-icon>
                  </button>
                  `
                      : html`
                    <button 
                      class="step-indicator__button"
                      @click=${() => this.handleChangeStep(indexValue)}>
                      ${indexValue}</button>
                  `
                  }
                `
                  : html`
                  ${
                    indexValue < this.indicator
                      ? html`
                    <div class="step-indicator__checked">
                      <outline-icon name="check-lg" library="system" size="18px"></outline-icon>
                    </div>
                  `
                      : html`
                  <div
                  class="step-indicator__number">
                  ${indexValue}</div>`
                  }`
              }
                ${
                  indexValue >= 1 && indexValue < this.panels.length
                    ? html`<span class="step-indicator__line"></span>`
                    : html`<span class="step-indicator__line transparent"></span>`
                }
            </div>
            <label class="step-indicator__label">${item.dataset.step}</label>
          </div>
        `;
      })}
    `;
  }

  createSectionPanels(): void {
    if (!this.panels) return;

    this.panels.map((panel, index) => {
      const section = document.createElement('section');
      section.classList.add('step-panel');
      index === 0 && section.classList.add('active');
      section.setAttribute('slot', 'step-panels');
      if (panel.parentNode) {
        panel.removeAttribute('slot');
        panel.parentNode.insertBefore(section, panel);
        section.appendChild(panel);
      }
    });
  }

  render(): TemplateResult {
    const classes = {
      'step-indicator': true,
    };

    return html`
      <section class=${classMap(classes)}>
      <idb-section-wrapper>
        <div class="step-indicators" slot="content">
          ${this.stepsTemplate()}
        </div>
      </idb-section-wrapper>
        
      <slot name="step-panels"></slot>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'idb-step-indicator': IdbStepIndicator;
  }
}
